import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Activity, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ActivityLog = () => {
  const { getAuthHeaders } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 30;

  useEffect(() => { fetchLogs(); }, [page, filter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (filter) params.append('entity_type', filter);
      const res = await axios.get(`${API}/activity-log?${params}`, { headers: getAuthHeaders() });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch { toast.error('Erro ao carregar log de atividades'); }
    finally { setLoading(false); }
  };

  const totalPages = Math.ceil(total / limit);

  const actionColor = (action) => {
    if (action.includes('excluiu')) return 'text-red-400';
    if (action.includes('criou')) return 'text-green-400';
    if (action.includes('concluiu') || action.includes('pago')) return 'text-blue-400';
    if (action.includes('atribuiu')) return 'text-purple-400';
    if (action.includes('alterou')) return 'text-amber-400';
    return 'text-slate-300';
  };

  const entityIcon = (type) => {
    const map = { projeto: 'folder', pagamento: 'dollar-sign', usuario: 'user', personagem: 'users' };
    return map[type] || 'activity';
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-6" data-testid="activity-log-page">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
            <Activity className="h-8 w-8 text-teal-400" />
            Log de Atividades
          </h1>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={filter}
              onChange={e => { setFilter(e.target.value); setPage(1); }}
              className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-teal-500"
              data-testid="log-filter"
            >
              <option value="">Todos</option>
              <option value="projeto">Projetos</option>
              <option value="pagamento">Pagamentos</option>
              <option value="usuario">Usuários</option>
              <option value="personagem">Personagens</option>
            </select>
          </div>
        </div>

        <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-teal-400"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-400">Nenhuma atividade registrada.</div>
          ) : (
            <div className="divide-y divide-slate-700/50">
              {logs.map(log => (
                <div key={log.id} className="px-5 py-3 hover:bg-slate-700/20 transition-colors flex items-start gap-4" data-testid={`log-${log.id}`}>
                  <div className="mt-1 w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center shrink-0 text-xs text-teal-400 font-bold uppercase">
                    {log.entity_type?.charAt(0) || 'A'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="text-white font-medium">{log.user_name}</span>
                      {' '}
                      <span className={actionColor(log.action)}>{log.action}</span>
                      {' '}
                      <span className="text-slate-400">{log.entity_type}</span>
                      {log.entity_name && (
                        <span className="text-teal-400 font-medium"> "{log.entity_name}"</span>
                      )}
                    </p>
                    {log.details && <p className="text-xs text-slate-500 mt-0.5">{log.details}</p>}
                  </div>
                  <span className="text-xs text-slate-500 shrink-0 whitespace-nowrap">{formatDate(log.created_at)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-700">
              <span className="text-sm text-slate-400">{total} registros</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg bg-slate-700 text-white disabled:opacity-30 hover:bg-slate-600 transition-colors"
                  data-testid="log-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-white">{page} / {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg bg-slate-700 text-white disabled:opacity-30 hover:bg-slate-600 transition-colors"
                  data-testid="log-next-page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ActivityLog;
